import React, { useEffect, useRef, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import Tabs, { Tab, TabList } from '@atlaskit/tabs';
import Badge from '@atlaskit/badge';
import { Text, Inline } from '@atlaskit/primitives';

import type { ConfluenceContentAccessRequest } from '@confluence/access-requests/entry-points/useAccessRequestsTypes';

const i18n = defineMessages({
	peopleWithAccessLabel: {
		id: 'share-and-restrict-dialog.specific-access-tabs.people-with-access',
		defaultMessage: 'People with access',
		description: 'Label for tab for people with access',
	},
	requestsLabel: {
		id: 'share-and-restrict-dialog.specific-access-tabs.requests',
		defaultMessage: 'Requests',
		description: 'Label for tab for Requests',
	},
});

export type SpecificAccessTabsProps = {
	tabSelection?: number;
	setTabSelection?: (tabSelection: number) => void;
	requests: (ConfluenceContentAccessRequest | null)[];
	refetchAccessRequest: () => void;
};

export const SpecificAccessTabs = ({
	setTabSelection,
	tabSelection,
	requests,
	refetchAccessRequest,
}: SpecificAccessTabsProps) => {
	const count = requests?.length ?? 0;
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

	// Debounced refetch for tab 0 (People with access):
	// - Batches rapid clicks on tab 0 into a single refetch call (500ms debounce)
	// - Only active when on tab 0; cancelled if user switches to tab 1
	// - Tab 1 (Requests) has its own query/subscription, so no refetch needed there
	const debouncedRefetch = useCallback(() => {
		// Clear existing timer
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		// Set new timer to refetch after 500ms of inactivity
		debounceTimerRef.current = setTimeout(() => {
			refetchAccessRequest();
		}, 500);
	}, [refetchAccessRequest]);

	useEffect(() => {
		// When switching to tab 1, cancel any pending refetch
		// Tab 1 (Requests) has its own query/subscription that keeps it fresh
		if (tabSelection === 1) {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
				debounceTimerRef.current = null;
			}
		} else {
			// For tab 0 and other tabs, trigger debounced refetch
			debouncedRefetch();
		}

		// Cleanup: cancel pending refetch on unmount
		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, [tabSelection, debouncedRefetch]);

	return (
		<Tabs onChange={setTabSelection} selected={tabSelection} id="controlled">
			<TabList>
				<Tab testId="specific-access-tab-people">
					<FormattedMessage {...i18n.peopleWithAccessLabel} />
				</Tab>
				<Tab testId="specific-access-tab-requests">
					<Inline space="space.075" alignBlock="center">
						<Text>
							<FormattedMessage {...i18n.requestsLabel} />
						</Text>
						{count ? (
							<Badge appearance="important" max={9}>
								{count}
							</Badge>
						) : null}
					</Inline>
				</Tab>
			</TabList>
		</Tabs>
	);
};
